import { redirect, type LoaderFunction } from 'react-router';
import { plantQueryOptions } from '@/entities/plant';
import { isPlantSubscribable } from '@/entities/subscription';
import { queryClient } from '@/shared/api';
import { ROUTES } from '@/shared/routing';
import { apiV2 } from '@/app/runtime';

/**
 * 구독유형 선택(`/subscriptions/:plantId`) 진입 가드 — **발전소 존재 + 구독 가능 검증 + prefetch**.
 *
 * loader 가 진입 _전_ 발전소를 한 번 조회(`ensureQueryData`, `include=subscription,order`)해 React Query
 * 캐시(`['plant', plantId]`)에 적재한다. 통과하면 페이지의 `usePlantQuery(plantId)` 가 **같은 캐시를 재사용**
 * — 별도 재호출 없음. list 경유든 직접 URL 진입이든 동일하게 검증된다.
 *
 * 차단 조건(모두 목록으로 redirect — 조용히 격하 않음):
 *  - 없는/접근 불가 plantId (조회 throw)
 *  - **이미 구독중**(미구독·해지·만료 아님) — `isPlantSubscribable` 로 판정. plan 은 신규/재구독 진입점.
 *  - **비대표**(`isRepresentative === false`) — 결제 권한이 없으면 결제 화면에 들어갈 이유가 없다.
 *    목록·마이페이지·정보상세의 버튼 게이팅은 **UI 차원**이라 URL 직접 입력으로 우회된다. 라우트에서
 *    한 번 더 막아야 진입 경로 전체가 닫힌다(최종 판정은 서버 몫 — 여기는 헛걸음 방지).
 *
 * api(`apiV2`)는 composition-root 가 router _뒤_ 에 만들어 모듈 eval 시점엔 미정의 →
 * **late-binding**(async 본문=네비게이션 시점에만 접근)으로 TDZ 순환을 피한다(navigation.guard 동일 원칙).
 */
export const planGuard: LoaderFunction = async ({ params }) => {
  const { plantId } = params;
  if (!plantId) return redirect(ROUTES.SUBSCRIPTIONS);
  let item;
  try {
    item = await queryClient.ensureQueryData(plantQueryOptions(apiV2, plantId));
  } catch {
    return redirect(ROUTES.SUBSCRIPTIONS);
  }
  // 이미 구독중인 발전소는 plan(신규·재구독) 진입 차단 — 목록으로.
  if (!isPlantSubscribable(item)) return redirect(ROUTES.SUBSCRIPTIONS);
  // 결제 권한(대표) 없는 계정 차단. 조회가 성공한 뒤라 값이 확정 — UI 게이팅과 달리 여기선 fail-open 하지
  // 않는다(조회 실패는 위 catch 가 이미 목록으로 보냄).
  if (!item.isRepresentative) return redirect(ROUTES.SUBSCRIPTIONS);
  return null;
};
